import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { setColorScheme } from '@rio-cloud/rio-uikit/ColorScheme';
import useMutationObserver from '@rio-cloud/rio-uikit/useMutationObserver';

import packageJson from '../../../package.json';
import {
    getThemeFromDocument,
    STYLE_DEFAULT,
    STYLE_INTERNATIONAL,
    STYLE_MAN,
    STYLE_RIO,
    STYLE_SCANIA,
    STYLE_TRATON,
    STYLE_VW,
    STYLE_WIREFRAMES,
    type ThemeMode,
} from '../header/Themes';

const CDN_URL = 'https://uikit.developers.rio.cloud';

const UIKIT_DEMO_THEME_CSS_ATTRIBUTE = 'data-uikit-demo-theme-css';
const UIKIT_DEMO_THEME_CSS_LOADING_ATTRIBUTE = 'data-uikit-demo-theme-css-loading';
const DARKMODE_CSS_PROPERTY = '--has-darkmode';

const themeCssFiles: Record<string, string> = {
    DEFAULT: 'uikit-theme-default.css',
    [STYLE_RIO]: 'uikit-theme-rio.css',
    [STYLE_WIREFRAMES]: 'uikit-theme-wireframes.css',
    [STYLE_MAN]: 'uikit-theme-man.css',
    [STYLE_SCANIA]: 'uikit-theme-scania.css',
    [STYLE_TRATON]: 'uikit-theme-traton.css',
    [STYLE_INTERNATIONAL]: 'uikit-theme-international.css',
    [STYLE_VW]: 'uikit-theme-vw.css',
    wireframes: 'uikit-theme-wireframes.css',
};

const themeClassNames = [
    'style-vw',
    'style-man',
    'style-scania',
    'style-traton',
    'style-international',
    'style-wireframes',
];

const themeCssFileNames = [...new Set(Object.values(themeCssFiles))];

const getNormalizedThemeStyle = (style: string) => style.toUpperCase().replace(/-/g, '_');

const getValidThemeMode = (mode: string | null): ThemeMode => (mode === 'dark' ? 'dark' : 'light');

const getThemeCssFile = (style: string) =>
    themeCssFiles[style] || themeCssFiles[style.toUpperCase()] || themeCssFiles[getNormalizedThemeStyle(style)];

const getThemeStyleFromDocument = () => {
    const { brand } = document.documentElement.dataset;

    return brand && getThemeCssFile(brand) ? getNormalizedThemeStyle(brand) : STYLE_DEFAULT;
};

const getThemeHasDarkmode = () =>
    getComputedStyle(document.documentElement).getPropertyValue(DARKMODE_CSS_PROPERTY).trim() === 'true';

const isThemeCssLink = (link: HTMLLinkElement) =>
    link.hasAttribute(UIKIT_DEMO_THEME_CSS_ATTRIBUTE) ||
    themeCssFileNames.some(fileName => link.href.includes(fileName));

const getThemeClassName = (style: string) => {
    const normalizedStyle = getNormalizedThemeStyle(style);

    if (normalizedStyle === STYLE_WIREFRAMES || style === 'wireframes') {
        return 'style-wireframes';
    }

    if (normalizedStyle === 'DEFAULT' || normalizedStyle === STYLE_RIO) {
        return null;
    }

    return `style-${style.toLowerCase()}`;
};

const applyThemeMode = (mode: ThemeMode, hasDarkmode: boolean) => {
    if (hasDarkmode) {
        document.documentElement.setAttribute('data-theme', mode);
        setColorScheme(mode);
        return;
    }

    document.documentElement.removeAttribute('data-theme');
    setColorScheme('default');
};

const waitForThemeLayout = () =>
    new Promise<void>(resolve => {
        const waitForVisibleBody = () => {
            const isWaitingForThemeCss =
                document.documentElement.hasAttribute('data-uikit-init') &&
                window.getComputedStyle(document.body).visibility !== 'visible';

            if (isWaitingForThemeCss) {
                window.requestAnimationFrame(waitForVisibleBody);
                return;
            }

            // The stylesheet load event can fire before Chrome has applied the new layout. Wait for one additional
            // frame and notify layout-dependent UIKIT components before synchronizing the React theme state.
            window.requestAnimationFrame(() => {
                window.dispatchEvent(new Event('resize'));
                resolve();
            });
        };

        window.requestAnimationFrame(waitForVisibleBody);
    });

const toggleTheme = (style = '', cssVersion = packageJson.version) =>
    new Promise<void>(resolve => {
        const html = document.documentElement;
        const isDev = import.meta.env.DEV;
        const themeCssFile = getThemeCssFile(style) || `${style.toLowerCase()}-uikit.css`;
        const linkRef = isDev ? `/uikit-theme-css/${themeCssFile}` : `${CDN_URL}/${cssVersion}/${themeCssFile}`;
        const resolvedLinkRef = new URL(linkRef, document.baseURI).href;

        const themeLinks = ([...document.head.querySelectorAll('link[rel="stylesheet"]')] as HTMLLinkElement[]).filter(
            isThemeCssLink
        );
        const matchingThemeLink = themeLinks.find(link => link.href === resolvedLinkRef);

        const updateThemeClass = () => {
            html.classList.remove(...themeClassNames);

            const themeClassName = getThemeClassName(style);
            if (themeClassName) {
                html.classList.add(themeClassName);
            }
        };

        const activateThemeLink = (activeThemeLink: HTMLLinkElement) => {
            const availableThemeLinks = (
                [...document.head.querySelectorAll('link[rel="stylesheet"]')] as HTMLLinkElement[]
            ).filter(isThemeCssLink);

            availableThemeLinks.forEach(themeLink => {
                themeLink.disabled = themeLink !== activeThemeLink;
            });
            updateThemeClass();
        };

        const resolveAfterThemeLayout = () => {
            void waitForThemeLayout().then(resolve);
        };

        if (matchingThemeLink) {
            if (matchingThemeLink.hasAttribute(UIKIT_DEMO_THEME_CSS_LOADING_ATTRIBUTE)) {
                matchingThemeLink.addEventListener('load', resolveAfterThemeLayout, { once: true });
                matchingThemeLink.addEventListener('error', () => resolve(), { once: true });
            } else {
                activateThemeLink(matchingThemeLink);
                resolveAfterThemeLayout();
            }

            return;
        }

        const link = document.createElement('link');
        const hasCurrentTheme = themeLinks.length > 0;

        link.type = 'text/css';
        link.rel = 'stylesheet';
        link.href = linkRef;
        link.setAttribute(UIKIT_DEMO_THEME_CSS_ATTRIBUTE, style);
        link.setAttribute(UIKIT_DEMO_THEME_CSS_LOADING_ATTRIBUTE, 'true');

        link.addEventListener(
            'load',
            () => {
                link.removeAttribute(UIKIT_DEMO_THEME_CSS_LOADING_ATTRIBUTE);
                activateThemeLink(link);
                resolveAfterThemeLayout();
            },
            { once: true }
        );
        link.addEventListener(
            'error',
            () => {
                link.setAttribute(UIKIT_DEMO_THEME_CSS_LOADING_ATTRIBUTE, 'error');
                console.error(`Could not load UIKIT demo theme stylesheet: ${link.href}`);
                resolve();
            },
            { once: true }
        );
        document.head.appendChild(link);

        if (!hasCurrentTheme) {
            activateThemeLink(link);
        }
    });

export const useTheme = () => {
    const { search } = useLocation();
    const navigate = useNavigate();

    const [theme, setTheme] = useState(getThemeStyleFromDocument);
    const [themeStyle, setThemeStyle] = useState(getThemeStyleFromDocument);
    const [themeMode, setThemeMode] = useState<ThemeMode>(getValidThemeMode(getThemeFromDocument()));
    const [hasDarkmode, setHasDarkmode] = useState(getThemeHasDarkmode);

    const themeStyleRef = useRef(themeStyle);

    const syncThemeState = (style: string, mode: ThemeMode) => {
        const normalizedStyle = getNormalizedThemeStyle(style);
        const themeHasDarkmode = getThemeHasDarkmode();
        const activeThemeMode = themeHasDarkmode ? mode : 'light';

        themeStyleRef.current = normalizedStyle;
        document.documentElement.dataset.brand = normalizedStyle.toLowerCase();
        setThemeStyle(normalizedStyle);
        setThemeMode(activeThemeMode);
        setHasDarkmode(themeHasDarkmode);
        setTheme(normalizedStyle);
        applyThemeMode(activeThemeMode, themeHasDarkmode);

        return themeHasDarkmode;
    };

    useEffect(() => {
        const searchParams = new URLSearchParams(search);
        const style = searchParams.get('style');
        const cssVersion = searchParams.get('version') || packageJson.version;
        const mode = getValidThemeMode(searchParams.get('theme') ?? getThemeFromDocument());
        const activeStyle = style || getThemeStyleFromDocument();

        void toggleTheme(activeStyle, cssVersion).then(() => {
            syncThemeState(activeStyle, mode);
        });
    }, [search]);

    const onThemeChange = useCallback(
        (themeWithMode: string, mode = themeMode) => {
            const newTheme = getNormalizedThemeStyle(themeWithMode);
            const searchParams = new URLSearchParams(search);
            const selectedVersion = packageJson.version;

            themeStyleRef.current = newTheme;

            searchParams.set('style', newTheme);
            if (searchParams.get('version') !== selectedVersion) {
                searchParams.set('version', selectedVersion);
            }
            searchParams.delete('css');

            void toggleTheme(newTheme, selectedVersion).then(() => {
                const themeHasDarkmode = syncThemeState(newTheme, mode);

                if (themeHasDarkmode) {
                    searchParams.set('theme', mode);
                } else {
                    searchParams.delete('theme');
                }

                navigate({ search: searchParams.toString() }, { replace: true });
            });
        },
        [navigate, search, themeMode]
    );

    const handleBrandChange = useCallback(
        (mutationList: MutationRecord[]) => {
            const hasBrandChange = mutationList.some(
                mutation => mutation.type === 'attributes' && mutation.attributeName === 'data-brand'
            );
            const brand = document.documentElement.dataset.brand;

            if (
                hasBrandChange &&
                brand &&
                getThemeCssFile(brand) &&
                getNormalizedThemeStyle(brand) !== themeStyleRef.current
            ) {
                onThemeChange(brand);
            }
        },
        [onThemeChange]
    );

    useMutationObserver(document.documentElement, handleBrandChange, {
        attributes: true,
        childList: false,
        subtree: false,
    });

    return {
        theme,
        themeStyle,
        themeMode,
        hasDarkmode,
        onThemeChange,
    };
};
